import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid2,
  useTheme,
  useMediaQuery,
  Fade,
} from '@mui/material';
import { isIOS, isAndroidPhone } from '../utils/deviceDetection';
import { extractUTMParameters, appendUTMToPlayStoreUrl } from '../utils/utm';
import { analytics } from '../services/analytics';
import { PLAY_STORE_URL, APP_STORE_URL } from '../constants/pwa';

// Import badge assets
import AppStoreBadge from '../assets/app-store-badge.svg';
import GooglePlayBadge from '../assets/google-play-badge.svg';
import AppQRiOS from '../assets/app-qr-ios.svg';
import AppQRAndroid from '../assets/app-qr-android.svg';

const AppDownload = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [redirecting, setRedirecting] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const iosDevice = isIOS();
    const androidPhone = isAndroidPhone();

    // Only auto-redirect for phones (not tablets)
    // Tablets get the desktop experience
    const shouldAutoRedirect = iosDevice || androidPhone;

    if (shouldAutoRedirect) {
      setRedirecting(true);

      // Extract UTM parameters from URL
      const utmParams = extractUTMParameters();

      // Determine platform and store URL
      const platform = iosDevice ? 'ios' : 'android';
      let storeUrl = iosDevice ? APP_STORE_URL : PLAY_STORE_URL;

      // For Android, append UTM parameters to Play Store URL via referrer
      if (androidPhone && Object.keys(utmParams).length > 0) {
        storeUrl = appendUTMToPlayStoreUrl(PLAY_STORE_URL, utmParams);
      }

      // Log analytics event with UTM parameters
      try {
        analytics.logAppStoreRedirect(platform, utmParams);
      } catch (error) {
        console.warn('Failed to log app store redirect:', error);
      }

      // Redirect to store (same window for potential native store opening)
      window.location.href = storeUrl;
    } else {
      // Show content with slight delay for smooth animation
      setTimeout(() => setShowContent(true), 100);
    }
  }, []);

  // Handle badge click for desktop/tablet users
  const handleBadgeClick = (platform: 'ios' | 'android') => {
    const utmParams = extractUTMParameters();

    let storeUrl = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;

    // For Android, append UTM parameters
    if (platform === 'android' && Object.keys(utmParams).length > 0) {
      storeUrl = appendUTMToPlayStoreUrl(PLAY_STORE_URL, utmParams);
    }

    // Log analytics
    try {
      analytics.logAppStoreRedirect(platform, utmParams);
    } catch (error) {
      console.warn('Failed to log app store redirect:', error);
    }

    // Open in new tab for desktop
    window.open(storeUrl, '_blank', 'noopener,noreferrer');
  };

  // Show minimal loading state while redirecting on mobile
  if (redirecting) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: '1rem',
              color: theme.palette.grey[700],
              letterSpacing: '0.01em',
            }}
          >
            Opening App Store...
          </Typography>
        </Box>
      </Fade>
    );
  }

  // Desktop/tablet experience - professional landing page
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        py: { xs: 5, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Fade in={showContent} timeout={400}>
          <Box
            sx={{
              textAlign: 'center',
              mb: { xs: 6, md: 8 },
              px: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.75rem' },
                color: theme.palette.grey[900],
                letterSpacing: '-0.02em',
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              Download CribCritic
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.25rem' },
                fontWeight: 400,
                color: theme.palette.grey[600],
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Your smart home companion for tracking maintenance, managing properties, and staying connected with your agent.
            </Typography>
          </Box>
        </Fade>

        {/* Download Cards */}
        <Grid2 container spacing={{ xs: 4, sm: 5, md: 6 }} justifyContent="center">
          {/* iOS App Store Card */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Fade in={showContent} timeout={400} style={{ transitionDelay: '100ms', height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, sm: 5, md: 6 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  backgroundColor: '#FFFFFF',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[4],
                  },
                }}
                onClick={() => handleBadgeClick('ios')}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    mb: 4,
                    color: theme.palette.grey[800],
                  }}
                >
                  For iPhone & iPad
                </Typography>

                {/* App Store Badge */}
                <Box
                  component="img"
                  src={AppStoreBadge}
                  alt="Download on the App Store"
                  sx={{
                    width: { xs: '180px', sm: '200px' },
                    height: 'auto',
                    mb: 4,
                  }}
                />

                {/* QR Code */}
                {!isMobile && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: '0.875rem',
                        color: theme.palette.grey[600],
                      }}
                    >
                      Or scan with your iPhone
                    </Typography>
                    <Box
                      component="img"
                      src={AppQRiOS}
                      alt="QR Code for iOS App"
                      sx={{
                        width: '180px',
                        height: '180px',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: '#FFFFFF',
                      }}
                    />
                  </>
                )}
              </Paper>
            </Fade>
          </Grid2>

          {/* Google Play Store Card */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Fade in={showContent} timeout={400} style={{ transitionDelay: '200ms', height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, sm: 5, md: 6 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  backgroundColor: '#FFFFFF',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[4],
                  },
                }}
                onClick={() => handleBadgeClick('android')}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    mb: 4,
                    color: theme.palette.grey[800],
                  }}
                >
                  For Android
                </Typography>

                {/* Google Play Badge */}
                <Box
                  component="img"
                  src={GooglePlayBadge}
                  alt="Get it on Google Play"
                  sx={{
                    width: { xs: '200px', sm: '220px' },
                    height: 'auto',
                    mb: 4,
                  }}
                />

                {/* QR Code */}
                {!isMobile && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: '0.875rem',
                        color: theme.palette.grey[600],
                      }}
                    >
                      Or scan with your Android device
                    </Typography>
                    <Box
                      component="img"
                      src={AppQRAndroid}
                      alt="QR Code for Android App"
                      sx={{
                        width: '180px',
                        height: '180px',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: '#FFFFFF',
                      }}
                    />
                  </>
                )}
              </Paper>
            </Fade>
          </Grid2>
        </Grid2>

        {/* Additional info */}
        <Fade in={showContent} timeout={400} style={{ transitionDelay: '300ms' }}>
          <Box
            sx={{
              mt: { xs: 8, sm: 10, md: 12 },
              textAlign: 'center',
              px: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                color: theme.palette.grey[600],
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Available for free on iOS and Android. Track your home maintenance, communicate with your agent, and access property insights on the go.
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default AppDownload;
